import React, { useState, useEffect } from 'react';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import AddIcon from "@mui/icons-material/Add";
import Tooltip from "@mui/material/Tooltip";
import PercentIcon from "@mui/icons-material/Percent";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Menu from "@mui/material/Menu";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { addClient, getAllClients, updateProjectStatus } from '../../api/clientApi';
import { useAuth } from '../../context/AuthContext';
import ClientBalanceSheet from './client_balance_sheet';
import LoadingGate from '../LoadingGate';
import BusinessIcon from '@mui/icons-material/Business';
import "../../styles.css";

// A small circular progress ring — used to wrap each client's avatar with a
// live "payment completion" indicator (received / contract value).
const RadialProgress = ({ pct, size = 46, stroke = 3, color, track = "#eef2f7", children }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const offset = c - (clamped / 100) * c;
  return (
    <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)", display: "block" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.7s cubic-bezier(0.22,1,0.36,1)" }}
        />
      </svg>
      <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {children}
      </Box>
    </Box>
  );
};

const AllClients = ({ resetToTable, accessLevel = 'full_access', onStatsReady, onDetailViewChange }) => {
  const { user, checkAuth } = useAuth();

  // If user_type is "main", grant full access to everything
  const isMainUser = user?.user_type === 'main';
  const isViewOnly = !isMainUser && accessLevel === 'view_only';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [fyMenuAnchor, setFyMenuAnchor] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  // Mobile: focused-search mode — hero/chips hide so the bar sits at the top
  const [searchFocused, setSearchFocused] = useState(false);
  const [financialYear, setFinancialYear] = useState(() => {
    // Auto-select current financial year based on date
    const today = new Date();
    const currentMonth = today.getMonth() + 1; // getMonth() returns 0-11, so add 1
    const currentYear = today.getFullYear();

    if (currentMonth >= 4) {
      // April to December: FY is current year to next year
      const nextYear = currentYear + 1;
      return `${currentYear}-${String(nextYear).slice(-2)}`;
    } else {
      // January to March: FY is previous year to current year
      const previousYear = currentYear - 1;
      return `${previousYear}-${String(currentYear).slice(-2)}`;
    }
  });
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [percentageFilterAnchorEl, setPercentageFilterAnchorEl] = useState(null);
  const [selectedPercentageFilter, setSelectedPercentageFilter] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [addClientDialogOpen, setAddClientDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    date: "",
    clientName: "",
    projectId: "",
    address: "",
    mobileNumber: "",
    emailId: ""
  });
  const [fieldErrors, setFieldErrors] = useState({
    date: "",
    clientName: "",
    address: "",
    mobileNumber: ""
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [statusMessage, setStatusMessage] = useState({
    show: false,
    text: '',
    severity: 'success'
  });

  // View state: 'table' or 'detail'
  const [view, setView] = useState('table');
  const [selectedClientData, setSelectedClientData] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  // Mobile pagination — cap the client list at 20, "Load More" reveals the rest
  const PAGE_SIZE = 20;
  const [mVisibleCount, setMVisibleCount] = useState(PAGE_SIZE);
  // Desktop table pagination — cap at 30/page
  const ALL_CLIENTS_PAGE_SIZE = 30;
  const [allClientsPage, setAllClientsPage] = useState(1);
  useEffect(() => {
    setAllClientsPage(1);
  }, [searchQuery, selectedStatus, selectedPercentageFilter, sortColumn, sortDirection]);

  // Reset to table view when resetToTable prop changes
  useEffect(() => {
    if (resetToTable !== undefined) {
      setView('table');
    }
  }, [resetToTable]);

  // Report detail-view state up so the shared page hero (Clients / tagline /
  // Summary·All Clients tabs) can hide itself while a client is open.
  useEffect(() => {
    if (onDetailViewChange) onDetailViewChange(view === 'detail');
  }, [view, onDetailViewChange]);

  // Re-sync the logged-in user's own record (incl. access_rights) every time a
  // client is opened — AuthContext only checks auth once at app boot, so a
  // sub-user's permissions edited by an admin mid-session would otherwise stay
  // stale until a full page reload. This keeps accessRights below fresh.
  useEffect(() => {
    if (view === 'detail') checkAuth();
  }, [view, selectedClientData]);

  const financialYears = ["Custom Date", "2024-25", "2025-26", "2026-27"];

  // Sample client data (commented out)
  const [clientData, setClientData] = useState([
    // {
    //   id: 1,
    //   name: "Infosys Limited",
    //   status: "completed",
    //   statusColor: "#59AC77",
    //   totalSales: 9500000,
    //   totalExpenses: 5500000,
    //   amountReceived: 8000000,
    //   balanceReceivable: 1500000,
    //   balancePayable: 500000,
    //   netProfit: 4000000,
    //   percentage: 42.11
    // }
  ]);

  const statusOptions = [
    { value: "all", label: "All", color: "#FFFFFF" },
    { value: "completed", label: "Completed", color: "#59AC77" },
    { value: "in-progress", label: "In-Progress", color: "#E8DE61" },
    { value: "not-started", label: "Not Yet Started", color: "#60BEF7" },
    { value: "dispute", label: "Project Under Dispute", color: "#FF8570" }
  ];

  const percentageFilterOptions = [
    { value: "all", label: "All", color: "#FFFFFF" },
    { value: "high-profit", label: "More than 40% Profit", color: "#59AC77" },
    { value: "low-profit", label: "Less than 40% Profit", color: "#E8DE61" },
    { value: "loss", label: "Loss Making Projects", color: "#FF8570" }
  ];

  // Fetch clients from MongoDB on component load and when view changes back to table
  useEffect(() => {
    const fetchClients = async () => {
      try {
        setPageLoading(true);
        // Calculate date range from financial year
        let queryFromDate = null;
        let queryToDate = null;

        if (financialYear && financialYear !== "Custom Date") {
          // Parse financial year (e.g., "2024-25")
          const [startYear, endYearShort] = financialYear.split('-');
          const endYear = `20${endYearShort}`;

          // Financial year: April 1 of startYear to March 31 of endYear
          queryFromDate = `${startYear}-04-01`;
          queryToDate = `${endYear}-03-31`;
        } else if (financialYear === "Custom Date" && fromDate && toDate) {
          // Use custom date range from state
          queryFromDate = fromDate;
          queryToDate = toDate;
        }

        // Prepare query parameters
        if (!user?.company_id) return;

        const params = {
          company_id: user.company_id
        };

        if (queryFromDate && queryToDate) {
          params.fromDate = queryFromDate;
          params.toDate = queryToDate;
        }

        const response = await getAllClients(params);
        if (response.success) {
          // Map MongoDB data to table format
          const mappedClients = response.data.map((client, index) => {
            const balanceSheet = client.client_balance_sheet || {};
            const summaryDetails = balanceSheet.summary_details || {};

            // Total Sales = Total Contract Value (Initial Contract Value + Additional Work Value)
            const incomeRelated = summaryDetails.income_related || {};
            const totalSales = (parseFloat(incomeRelated['Initial Contract Value']) || 0) +
                              (parseFloat(incomeRelated['Additional Work Value']) || 0);

            // Total Expenses
            const expensesRelated = summaryDetails.expenses_related || {};
            const totalExpenses = (parseFloat(expensesRelated['Total Material Purchases']) || 0) +
                                 (parseFloat(expensesRelated['Total Labour Charges']) || 0);

            // Balance Receivable and Balance Payable from summary
            const balancesRelated = summaryDetails.balances_related || {};
            const balanceReceivable = parseFloat(balancesRelated['Balance Receivable']) || 0;
            const balancePayable = parseFloat(balancesRelated['Balance Payable']) || 0;

            // Amount Received = Total Contract Value - Balance Receivable
            const amountReceived = totalSales - balanceReceivable;

            // Gross Profit
            const netProfit = totalSales - totalExpenses;

            // Percentage
            const percentage = totalSales > 0 ? parseFloat(((netProfit / totalSales) * 100).toFixed(2)) : 0;

            // Get project status from MongoDB, default to "not-started" if not found
            const projectStatus = client.project_status || "not-started";
            const statusColor = statusOptions.find(opt => opt.value === projectStatus)?.color || "#60BEF7";

            return {
              id: client._id,
              name: client.client_name,
              status: projectStatus,
              statusColor: statusColor,
              totalSales,
              totalExpenses,
              amountReceived,
              balanceReceivable,
              balancePayable,
              netProfit,
              percentage,
              createdAt: client.createdAt // Keep creation date for FIFO ordering
            };
          });

          // Reverse to show newest first (FIFO order)
          setClientData(mappedClients.reverse());
        }
      } catch (error) {
        console.error("Error fetching clients:", error);
      } finally {
        setPageLoading(false);
      }
    };

    // Fetch data when component loads or when returning to table view or when financial year changes
    if (view === 'table') {
      fetchClients();
    }
  }, [view, financialYear, fromDate, toDate, user]);

  // Reset mobile pagination whenever the visible dataset changes
  useEffect(() => {
    setMVisibleCount(PAGE_SIZE);
  }, [searchQuery, selectedStatus, selectedPercentageFilter, financialYear]);

  const handleClearFinancialYear = () => {
    setFinancialYear("");
    setFromDate("");
    setToDate("");
    // This will trigger useEffect to fetch all clients without date filter
  };

  const handleCustomDateSearch = () => {
    if (fromDate && toDate) {
      setFinancialYear("Custom Date");
      setCustomDateDialogOpen(false);
      // This will trigger the useEffect to fetch clients with custom date range
    }
  };

  const handleCustomDateCancel = () => {
    setCustomDateDialogOpen(false);
  };

  const handleAddClient = () => {
    setAddClientDialogOpen(true);
  };

  const handleAddClientClose = () => {
    setAddClientDialogOpen(false);
    setNewClient({
      date: "",
      clientName: "",
      projectId: "",
      address: "",
      mobileNumber: "",
      emailId: ""
    });
    setFieldErrors({
      date: "",
      clientName: "",
      address: "",
      mobileNumber: ""
    });
  };

  const handleNewClientChange = (field, value) => {
    let processedValue = value;

    // Capitalize first letter of each word for clientName
    if (field === 'clientName') {
      processedValue = value
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }

    setNewClient(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Clear error for this field when user types
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const handleAddClientNext = async () => {
    // Validate all fields
    const errors = {
      date: "",
      clientName: "",
      address: "",
      mobileNumber: ""
    };

    let hasError = false;

    if (newClient.date.trim() === "") {
      errors.date = "Date is required";
      hasError = true;
    }
    if (newClient.clientName.trim() === "") {
      errors.clientName = "Client Name is required";
      hasError = true;
    }
    if (newClient.address.trim() === "") {
      errors.address = "Address is required";
      hasError = true;
    }
    if (newClient.mobileNumber.trim() === "") {
      errors.mobileNumber = "Mobile Number is required";
      hasError = true;
    }

    if (hasError) {
      setFieldErrors(errors);
      return;
    }

    try {
      setLoading(true);
      const response = await addClient(newClient);

      if (response.success) {
        setSnackbar({
          open: true,
          message: "Client added successfully!",
          severity: "success"
        });
        handleAddClientClose();

        // Map response data to match expected format
        const mappedClientData = {
          ...response.data,
          name: response.data.client_name,
          id: response.data._id
        };

        // Switch to detail view
        setSelectedClientData(mappedClientData);
        setView('detail');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Error adding client. Please try again.",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const [statusChangeAnchorEl, setStatusChangeAnchorEl] = useState(null);
  const [selectedClientForStatusChange, setSelectedClientForStatusChange] = useState(null);

  const handleStatusFilterClick = (event) => {
    setStatusAnchorEl(event.currentTarget);
  };

  const handleStatusFilterClose = () => {
    setStatusAnchorEl(null);
  };

  const handleStatusFilterSelect = (status) => {
    setSelectedStatus(status === "all" ? null : status);
    setStatusAnchorEl(null);
  };

  const handleStatusCellClick = (event, clientId) => {
    setStatusChangeAnchorEl(event.currentTarget);
    setSelectedClientForStatusChange(clientId);
  };

  const handleStatusChangeClose = () => {
    setStatusChangeAnchorEl(null);
    setSelectedClientForStatusChange(null);
  };

  const handleStatusChange = async (newStatus) => {
    if (selectedClientForStatusChange && newStatus !== "all") {
      try {
        // Update status in MongoDB
        await updateProjectStatus(selectedClientForStatusChange, newStatus);

        // Update local state
        setClientData(prevData =>
          prevData.map(client =>
            client.id === selectedClientForStatusChange
              ? {
                  ...client,
                  status: newStatus,
                  statusColor: statusOptions.find(opt => opt.value === newStatus)?.color || client.statusColor
                }
              : client
          )
        );

        setStatusMessage({
          show: true,
          text: "Project status updated successfully!",
          severity: "success"
        });

        // Auto-hide message after 3 seconds
        setTimeout(() => {
          setStatusMessage({ show: false, text: '', severity: 'success' });
        }, 3000);
      } catch (error) {
        setStatusMessage({
          show: true,
          text: error.message || "Error updating project status.",
          severity: "error"
        });

        // Auto-hide message after 3 seconds
        setTimeout(() => {
          setStatusMessage({ show: false, text: '', severity: 'success' });
        }, 3000);
      }
    }
    setStatusChangeAnchorEl(null);
    setSelectedClientForStatusChange(null);
  };

  const handlePercentageFilterClick = (event) => {
    event.stopPropagation();
    setPercentageFilterAnchorEl(event.currentTarget);
  };

  const handlePercentageFilterClose = () => {
    setPercentageFilterAnchorEl(null);
  };

  const handlePercentageFilterSelect = (filter) => {
    setSelectedPercentageFilter(filter === "all" ? null : filter);
    setPercentageFilterAnchorEl(null);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const formatCurrency = (amount) => {
    return `₹ ${Math.round(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  // Compact ₹ for mobile cards (handles Cr/L and negatives)
  const compactINR = (n) => {
    const num = Number(n) || 0;
    const neg = num < 0 ? '-' : '';
    const a = Math.abs(num);
    if (a >= 10000000) return `${neg}₹${(a / 10000000).toFixed(2)} Cr`;
    if (a >= 100000) return `${neg}₹${(a / 100000).toFixed(2)} L`;
    return `${neg}₹${a.toLocaleString('en-IN')}`;
  };
  const statusLabelOf = (value) => (statusOptions.find((o) => o.value === value) || {}).label || 'Not Yet Started';
  const currentChangeStatus = (clientData.find((c) => c.id === selectedClientForStatusChange) || {}).status;
  // A stunning bottom-sheet option row (mobile)
  const renderSheetOption = (key, color, label, selected, onClick) => (
    <Box
      key={key}
      onClick={onClick}
      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.4, px: 1.25, borderRadius: '14px', cursor: 'pointer', transition: 'background 0.15s ease', background: selected ? '#eff5ff' : 'transparent', '&:active': { background: '#e8f1fe' } }}
    >
      <Box sx={{ width: 22, height: 22, borderRadius: '7px', flexShrink: 0, background: color === '#FFFFFF' ? '#f2f4f7' : color, border: color === '#FFFFFF' ? '1px solid #e4e7ec' : 'none' }} />
      <Typography sx={{ flex: 1, fontSize: '14px', fontWeight: selected ? 700 : 500, color: selected ? '#1f6fd6' : '#344054', fontFamily: 'Roboto, Arial, sans-serif' }}>{label}</Typography>
      {selected && <CheckIcon sx={{ fontSize: 20, color: '#39aafc' }} />}
    </Box>
  );
  const sheetPaperSx = { borderTopLeftRadius: '24px', borderTopRightRadius: '24px', px: 2.5, pt: 1.5, pb: 'max(20px, env(safe-area-inset-bottom))' };
  const topSheetPaperSx = { borderBottomLeftRadius: '24px', borderBottomRightRadius: '24px', px: 2.5, pt: 'max(18px, env(safe-area-inset-top))', pb: 2, boxShadow: '0 10px 30px rgba(16,24,40,0.15)' };
  const sheetHandle = <Box sx={{ width: 40, height: 4, borderRadius: 2, background: '#e0e3e8', mx: 'auto', mb: 2 }} />;
  const bottomHandle = <Box sx={{ width: 40, height: 4, borderRadius: 2, background: '#e0e3e8', mx: 'auto', mt: 2 }} />;
  const sheetTitleSx = { fontSize: '16px', fontWeight: 700, color: '#101828', mb: 1, fontFamily: 'Roboto, Arial, sans-serif' };
  // A stunning bottom-sheet option row with no color swatch (plain text lists like Financial Year)
  const renderPlainSheetOption = (key, label, selected, onClick) => (
    <Box
      key={key}
      onClick={onClick}
      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.4, px: 1.25, borderRadius: '14px', cursor: 'pointer', transition: 'background 0.15s ease', background: selected ? '#eff5ff' : 'transparent', '&:active': { background: '#e8f1fe' } }}
    >
      <Typography sx={{ flex: 1, fontSize: '14px', fontWeight: selected ? 700 : 500, color: selected ? '#1f6fd6' : '#344054', fontFamily: 'Roboto, Arial, sans-serif' }}>{label}</Typography>
      {selected && <CheckIcon sx={{ fontSize: 20, color: '#39aafc' }} />}
    </Box>
  );
  const addFieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: '14px', backgroundColor: '#fff', '& fieldset': { borderColor: '#e4e7ec' }, '&:hover fieldset': { borderColor: '#39aafc' }, '&.Mui-focused fieldset': { borderColor: '#39aafc', borderWidth: '1.5px' } },
    '& .MuiInputBase-input': { fontSize: '16px', fontFamily: 'Roboto, Arial, sans-serif' },
    '& .MuiInputLabel-root': { fontFamily: 'Roboto, Arial, sans-serif' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#39aafc' },
    '& .MuiFormHelperText-root': { fontFamily: 'Roboto, Arial, sans-serif' },
  };

  const getFilteredData = () => {
    let filtered = clientData;

    // Filter by search query
    if (searchQuery.trim() !== "") {
      filtered = filtered.filter(client =>
        client.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedStatus) {
      filtered = filtered.filter(client => client.status === selectedStatus);
    }

    if (selectedPercentageFilter) {
      filtered = filtered.filter(client => {
        if (selectedPercentageFilter === "high-profit") {
          return client.percentage >= 40;
        } else if (selectedPercentageFilter === "low-profit") {
          return client.percentage >= 0 && client.percentage < 40;
        } else if (selectedPercentageFilter === "loss") {
          return client.percentage < 0;
        }
        return true;
      });
    }

    return filtered;
  };

  const getSortedData = () => {
    const filtered = getFilteredData();

    // If no sort column is explicitly picked, give In-Progress clients
    // priority — they show first, everyone else after — while still keeping
    // every client visible (this is a priority ordering, not a filter).
    // Array.prototype.sort is stable, so within each group the original
    // (FIFO/newest-first) order is preserved.
    if (!sortColumn) {
      const priority = (c) => (c.status === 'in-progress' ? 0 : 1);
      return [...filtered].sort((a, b) => priority(a) - priority(b));
    }

    const sorted = [...filtered].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      if (sortDirection === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    return sorted;
  };

  // Report the portfolio KPI totals up so the shared page hero can render
  // its own stat cards (same pattern Summary uses) — no duplicate UI here.
  useEffect(() => {
    if (!onStatsReady) return;
    const rows = getSortedData();
    const k = rows.reduce(
      (acc, c) => ({
        sales: acc.sales + c.totalSales,
        received: acc.received + c.amountReceived,
        receivable: acc.receivable + c.balanceReceivable,
        profit: acc.profit + c.netProfit,
      }),
      { sales: 0, received: 0, receivable: 0, profit: 0 }
    );
    onStatsReady(k);
  }, [clientData, searchQuery, selectedStatus, selectedPercentageFilter, sortColumn, sortDirection, onStatsReady]);

  const getPercentageColor = (percentage) => {
    if (percentage < 0) {
      return "#FF8570";
    } else if (percentage >= 40) {
      return "#59AC77";
    } else {
      return "#E8DE61";
    }
  };

  // Client Balance Sheet View
  if (view === 'detail' && selectedClientData) {
    return (
      <ClientBalanceSheet
        clientData={selectedClientData}
        onBack={() => setView('table')}
        accessRights={{
          basicDetailsTab: isMainUser ? 'full_access' : (user?.access_rights?.basicDetailsTab || 'no_access'),
          clientSummaryTab: isMainUser ? 'full_access' : (user?.access_rights?.clientSummaryTab || 'no_access'),
          clientOverviewTab: isMainUser ? 'full_access' : (user?.access_rights?.clientOverviewTab || 'no_access'),
          deleteClient: isMainUser ? 'full_access' : (user?.access_rights?.deleteClient || 'no_access'),
          downloadClientBalanceSheet: isMainUser ? 'full_access' : (user?.access_rights?.downloadClientBalanceSheet || 'no_access'),
          incomeDetailsExpandable: isMainUser ? 'full_access' : (user?.access_rights?.incomeDetailsExpandable || 'no_access'),
          initialContractValueBreakupTable: isMainUser ? 'full_access' : (user?.access_rights?.initialContractValueBreakupTable || 'no_access'),
          additionalWorkValueBreakup: isMainUser ? 'full_access' : (user?.access_rights?.additionalWorkValueBreakup || 'no_access'),
          expensesDetailsExpandable: isMainUser ? 'full_access' : (user?.access_rights?.expensesDetailsExpandable || 'no_access'),
          materialPurchasesTable: isMainUser ? 'full_access' : (user?.access_rights?.materialPurchasesTable || 'no_access'),
          labourCharges: isMainUser ? 'full_access' : (user?.access_rights?.labourCharges || 'no_access')
        }}
      />
    );
  }

  // Sortable header cell — icon (ties back to the KPI cards) + label + a
  // sort arrow that only appears, colored, on the currently-active column.
  // Grid-cell sort header (the ledger below is CSS Grid, not a <table>)
  const SortHeader = ({ column, icon: Icon, children }) => {
    const active = sortColumn === column;
    return (
      <Box
        role="columnheader"
        onClick={() => handleSort(column)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.6,
          cursor: "pointer",
          userSelect: "none",
          "&:hover .axd-sort-arrow": { opacity: 1 },
        }}
      >
        <Icon sx={{ fontSize: "13.5px", color: active ? "#1565C0" : "#9aa7b8" }} />
        <Box
          component="span"
          sx={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: active ? "#1565C0" : "#5f6f86", fontFamily: "Roboto, Arial, sans-serif" }}
        >
          {children}
        </Box>
        {active ? (
          sortDirection === "asc"
            ? <KeyboardArrowUpIcon sx={{ fontSize: "15px", color: "#1565C0" }} />
            : <KeyboardArrowDownIcon sx={{ fontSize: "15px", color: "#1565C0" }} />
        ) : (
          <KeyboardArrowUpIcon className="axd-sort-arrow" sx={{ fontSize: "15px", color: "#c3cbd6", opacity: 0, transition: "opacity 0.15s ease" }} />
        )}
      </Box>
    );
  };

  // Shared column template — header and every row use this so cells align perfectly
  const LEDGER_GRID_COLS = "minmax(230px, 2.1fr) minmax(110px, 1fr) minmax(100px, 0.9fr) minmax(110px, 1fr) minmax(135px, 1.15fr) minmax(115px, 1fr) minmax(150px, 1.3fr)";

  // Desktop table pagination — the row LIST is sliced to 30/page; the count
  // badge and Portfolio Total band below still summarize the FULL filtered
  // set (getSortedData()), not just the current page.
  const allClientsSorted = getSortedData();
  const allClientsTotalPages = Math.max(1, Math.ceil(allClientsSorted.length / ALL_CLIENTS_PAGE_SIZE));
  const allClientsPageClamped = Math.min(allClientsPage, allClientsTotalPages);
  const allClientsPageStart = (allClientsPageClamped - 1) * ALL_CLIENTS_PAGE_SIZE;
  const allClientsPaged = allClientsSorted.slice(allClientsPageStart, allClientsPageStart + ALL_CLIENTS_PAGE_SIZE);

  // Table View (default)
  return (
    <Box>
      {pageLoading && <LoadingGate icon={BusinessIcon} message="Loading clients…" />}
      {isMobile && (() => {
        const data = getSortedData();
        const mVisibleData = data.slice(0, mVisibleCount);
        const mHasMore = data.length > mVisibleCount;
        const totals = data.reduce((acc, c) => ({
          sales: acc.sales + c.totalSales,
          profit: acc.profit + c.netProfit,
          received: acc.received + c.amountReceived,
        }), { sales: 0, profit: 0, received: 0 });
        const overallPct = totals.sales > 0 ? ((totals.profit / totals.sales) * 100).toFixed(1) : '0';
        return (
          <Box sx={{ pb: '30px' }}>
            {/* Portfolio hero — steps aside while searching */}
            {!searchFocused && (
            <Box sx={{ background: 'linear-gradient(150deg, #4db2fc 0%, #1f8fe8 100%)', color: '#fff', borderRadius: '20px', p: 2.5, mb: 2, boxShadow: '0 10px 26px rgba(31,143,232,0.3)', position: 'relative', overflow: 'hidden' }}>
              <Box sx={{ position: 'absolute', top: '-40px', right: '-30px', width: '130px', height: '130px', borderRadius: '50%', background: 'rgba(255,255,255,0.12)' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
                <Typography sx={{ fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', opacity: 0.9, fontFamily: 'Roboto, Arial, sans-serif' }}>
                  Total Sales
                </Typography>
                <Box sx={{ background: 'rgba(255,255,255,0.2)', borderRadius: '20px', px: 1.25, py: 0.4, fontSize: '11.5px', fontWeight: 700, fontFamily: 'Roboto, Arial, sans-serif' }}>
                  {data.length} {data.length === 1 ? 'Project' : 'Projects'}
                </Box>
              </Box>
              <Typography sx={{ fontSize: '30px', fontWeight: 800, mt: 0.5, lineHeight: 1.1, fontFamily: 'Roboto, Arial, sans-serif' }}>
                {compactINR(totals.received)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2.5, mt: 1.5 }}>
                <Box>
                  <Typography sx={{ fontSize: '10.5px', opacity: 0.85, fontFamily: 'Roboto, Arial, sans-serif' }}>Gross Profit</Typography>
                  <Typography sx={{ fontSize: '15px', fontWeight: 700, fontFamily: 'Roboto, Arial, sans-serif' }}>{compactINR(totals.profit)}</Typography>
                </Box>
                <Box sx={{ width: '1px', background: 'rgba(255,255,255,0.25)' }} />
                <Box>
                  <Typography sx={{ fontSize: '10.5px', opacity: 0.85, fontFamily: 'Roboto, Arial, sans-serif' }}>Contract Value</Typography>
                  <Typography sx={{ fontSize: '15px', fontWeight: 700, fontFamily: 'Roboto, Arial, sans-serif' }}>{compactINR(totals.sales)}</Typography>
                </Box>
                <Box sx={{ width: '1px', background: 'rgba(255,255,255,0.25)' }} />
                <Box>
                  <Typography sx={{ fontSize: '10.5px', opacity: 0.85, fontFamily: 'Roboto, Arial, sans-serif' }}>Margin</Typography>
                  <Typography sx={{ fontSize: '15px', fontWeight: 700, fontFamily: 'Roboto, Arial, sans-serif' }}>{overallPct}%</Typography>
                </Box>
              </Box>
            </Box>
            )}

            {/* Search — focusing hides the hero/chips; closing the keyboard restores them */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, background: '#fff', border: '1px solid', borderColor: searchFocused ? '#39aafc' : '#eaecf0', borderRadius: '14px', px: 1.5, mb: 1.5, boxShadow: searchFocused ? '0 0 0 3px rgba(57,170,252,0.12)' : '0 1px 2px rgba(16,24,40,0.04)', transition: 'border-color 0.2s ease, box-shadow 0.2s ease' }}>
              <SearchIcon sx={{ fontSize: 20, color: searchFocused ? '#39aafc' : '#98a2b3' }} />
              <InputBase
                placeholder="Search clients…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => {
                  setSearchFocused(true);
                  setTimeout(() => { const sc = document.querySelector('.main-content'); if (sc) sc.scrollTo({ top: 0, behavior: 'smooth' }); }, 60);
                }}
                onBlur={() => setSearchFocused(false)}
                sx={{ flex: 1, fontSize: '14px', py: 1.25, fontFamily: 'Roboto, Arial, sans-serif' }}
              />
            </Box>

            {/* Filter chips — hidden while searching */}
            {!searchFocused && (
            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5, mb: 1.5, '&::-webkit-scrollbar': { display: 'none' } }}>
              {[
                { active: !!financialYear, onClick: (e) => setFyMenuAnchor(e.currentTarget), label: financialYear === 'Custom Date' ? 'Custom' : (financialYear || 'All Years') },
                { active: !!selectedStatus, onClick: handleStatusFilterClick, label: selectedStatus ? statusLabelOf(selectedStatus) : 'Status' },
                { active: !!selectedPercentageFilter, onClick: handlePercentageFilterClick, label: selectedPercentageFilter ? (percentageFilterOptions.find((o) => o.value === selectedPercentageFilter) || {}).label : 'Profit %' },
              ].map((c, i) => (
                <Box key={i} onClick={c.onClick} sx={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 0.5, borderRadius: '20px', px: 1.4, py: 0.85, fontSize: '12.5px', fontWeight: 700, fontFamily: 'Roboto, Arial, sans-serif', cursor: 'pointer', whiteSpace: 'nowrap', border: '1px solid', borderColor: c.active ? '#39aafc' : '#e4e7ec', color: c.active ? '#1f6fd6' : '#475467', background: c.active ? '#eff5ff' : '#fff', '&:active': { transform: 'scale(0.97)' } }}>
                  {i === 0 ? <CalendarMonthIcon sx={{ fontSize: 16 }} /> : <FilterAltIcon sx={{ fontSize: 16 }} />}
                  {c.label}
                  {i === 0 && <KeyboardArrowDownIcon sx={{ fontSize: 17 }} />}
                </Box>
              ))}
            </Box>
            )}

            {/* Client cards */}
            {data.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, color: '#98a2b3' }}>
                <Typography sx={{ fontSize: '14px', fontFamily: 'Roboto, Arial, sans-serif' }}>No clients found</Typography>
              </Box>
            ) : (
              mVisibleData.map((client) => (
                <Box
                  key={client.id}
                  onClick={() => { setSelectedClientData(client); setView('detail'); }}
                  sx={{ background: '#fff', borderRadius: '18px', p: 2, mb: 1.5, boxShadow: '0 2px 10px rgba(16,24,40,0.06)', border: '1px solid #f0f1f3', cursor: 'pointer', transition: 'transform 0.12s ease', '&:active': { transform: 'scale(0.985)' } }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography noWrap sx={{ fontSize: '15.5px', fontWeight: 700, color: '#101828', fontFamily: 'Roboto, Arial, sans-serif' }}>
                        {client.name}
                      </Typography>
                      <Box
                        onClick={isViewOnly ? undefined : (e) => { e.stopPropagation(); handleStatusCellClick(e, client.id); }}
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, mt: 0.5, cursor: isViewOnly ? 'default' : 'pointer' }}
                      >
                        <Box sx={{ width: '9px', height: '9px', borderRadius: '50%', background: client.statusColor, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '11.5px', fontWeight: 600, color: '#667085', fontFamily: 'Roboto, Arial, sans-serif' }}>
                          {statusLabelOf(client.status)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ flexShrink: 0, background: getPercentageColor(client.percentage), color: '#1a1a1a', borderRadius: '10px', px: 1.1, py: 0.5, fontSize: '13px', fontWeight: 800, fontFamily: 'Roboto, Arial, sans-serif' }}>
                      {client.percentage}%
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 1.25, columnGap: 1 }}>
                    {[
                      { label: 'Total Sales', value: client.amountReceived, color: '#101828' },
                      { label: 'Gross Profit', value: client.netProfit, color: client.netProfit < 0 ? '#d92d20' : '#059669' },
                      { label: 'Receivable', value: client.balanceReceivable, color: '#101828' },
                      { label: 'Payable', value: client.balancePayable, color: '#101828' },
                    ].map((m) => (
                      <Box key={m.label}>
                        <Typography sx={{ fontSize: '10.5px', color: '#98a2b3', fontWeight: 600, fontFamily: 'Roboto, Arial, sans-serif' }}>{m.label}</Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: m.color, fontFamily: 'Roboto, Arial, sans-serif' }}>{compactINR(m.value)}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))
            )}
            {mHasMore && (
              <Box
                onClick={() => setMVisibleCount((c) => c + PAGE_SIZE)}
                sx={{ textAlign: 'center', py: 1.4, mt: 0.5, mb: 1.5, borderRadius: '14px', border: '1.5px dashed #b8dffb', color: '#1565C0', fontWeight: 700, fontSize: '13.5px', cursor: 'pointer', '&:active': { background: '#f2f8ff' } }}
              >
                Load More ({data.length - mVisibleData.length} remaining)
              </Box>
            )}

            {/* Add Client FAB */}
            {!isViewOnly && (
              <Box
                onClick={handleAddClient}
                sx={{ position: 'fixed', right: '20px', bottom: 'calc(96px + env(safe-area-inset-bottom))', zIndex: 1200, width: 56, height: 56, borderRadius: '18px', background: 'linear-gradient(135deg, #39aafc 0%, #1f8fe8 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 24px rgba(31,143,232,0.5)', cursor: 'pointer', transition: 'transform 0.15s ease', '&:active': { transform: 'scale(0.92)' } }}
              >
                <AddIcon sx={{ fontSize: 28 }} />
              </Box>
            )}
          </Box>
        );
      })()}

      {!isMobile && (
      <>
      {/* KPI cards now render in the page hero above (see clients.js) —
          fed by onStatsReady, same pattern Summary uses. */}

      {/* ===== Table card — the table owns its header zone ===== */}
      <Box
        sx={{
          background: "#ffffff",
          border: "1px solid #e9eef5",
          borderRadius: "18px",
          boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 24px rgba(13,43,92,0.05)",
          overflow: "hidden",
        }}
      >
        {/* Header zone: identity left, tools right */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: "10px 20px", borderBottom: "1px solid #eef2f7" }}>
          <Typography sx={{ fontSize: "15.5px", fontWeight: 800, color: "#101828", letterSpacing: "-0.01em", fontFamily: "Roboto, Arial, sans-serif" }}>
            Portfolio
          </Typography>
          <Box sx={{ px: 1.25, py: "3px", borderRadius: "999px", background: "rgba(57,170,252,0.10)", color: "#1565C0", fontSize: "11.5px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif" }}>
            {getSortedData().length}
          </Box>
          {statusMessage.show && (
            <Typography sx={{ fontSize: "12.5px", fontWeight: 600, fontFamily: "Roboto, Arial, sans-serif", color: statusMessage.severity === 'success' ? '#059669' : '#d92d20' }}>
              {statusMessage.text}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />

          <Box className="client-search-bar" sx={{ width: "230px !important" }}>
            <SearchIcon className="search-icon-wrapper" />
            <InputBase
              className="styled-input-base"
              placeholder="Search clients…"
              inputProps={{ "aria-label": "search" }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Box>

          {/* FY selector — a real custom pill + menu, not a bordered MUI Select */}
          <Box
            onClick={(e) => setFyMenuAnchor(e.currentTarget)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              background: fyMenuAnchor ? "#eef6ff" : "#ffffff",
              border: `1px solid ${fyMenuAnchor ? "#39aafc" : "#e4e7ec"}`,
              borderRadius: "12px",
              px: 1.75,
              py: "7px",
              fontSize: "13px",
              fontWeight: 700,
              color: financialYear ? "#1565C0" : "#5f6f86",
              fontFamily: "Roboto, Arial, sans-serif",
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "all 0.18s ease",
              "&:hover": { borderColor: "#39aafc", background: "#eef6ff" },
            }}
          >
            <CalendarMonthIcon sx={{ fontSize: 16, color: financialYear ? "#1565C0" : "#9aa7b8" }} />
            {financialYear === "Custom Date" ? "Custom" : financialYear ? `FY ${financialYear}` : "All Years"}
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#9aa7b8", transition: "transform 0.2s ease", transform: fyMenuAnchor ? "rotate(180deg)" : "none" }} />
          </Box>

          {/* Filters — status + profit, out of the headers where they belong */}
          <Tooltip title={selectedStatus ? `Status: ${statusLabelOf(selectedStatus)}` : "Filter by status"}>
            <IconButton
              onClick={handleStatusFilterClick}
              size="small"
              sx={{
                width: 36,
                height: 36,
                borderRadius: "11px",
                position: "relative",
                border: selectedStatus ? "1px solid transparent" : "1px solid #e4e7ec",
                color: selectedStatus ? "#ffffff" : "#5f6f86",
                background: selectedStatus ? "linear-gradient(135deg, #4db2fc, #1565C0)" : "#ffffff",
                boxShadow: selectedStatus ? "0 6px 16px rgba(21,101,192,0.32)" : "none",
                transition: "all 0.18s ease",
                "&:hover": selectedStatus
                  ? { background: "linear-gradient(135deg, #39aafc, #0d47a1)", boxShadow: "0 8px 20px rgba(21,101,192,0.4)" }
                  : { borderColor: "#39aafc", color: "#1565C0", background: "rgba(57,170,252,0.08)" },
              }}
            >
              <FilterAltIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={selectedPercentageFilter ? "Profit filter active" : "Filter by profit %"}>
            <IconButton
              onClick={handlePercentageFilterClick}
              size="small"
              sx={{
                width: 36,
                height: 36,
                borderRadius: "11px",
                position: "relative",
                border: selectedPercentageFilter ? "1px solid transparent" : "1px solid #e4e7ec",
                color: selectedPercentageFilter ? "#ffffff" : "#5f6f86",
                background: selectedPercentageFilter ? "linear-gradient(135deg, #4db2fc, #1565C0)" : "#ffffff",
                boxShadow: selectedPercentageFilter ? "0 6px 16px rgba(21,101,192,0.32)" : "none",
                transition: "all 0.18s ease",
                "&:hover": selectedPercentageFilter
                  ? { background: "linear-gradient(135deg, #39aafc, #0d47a1)", boxShadow: "0 8px 20px rgba(21,101,192,0.4)" }
                  : { borderColor: "#39aafc", color: "#1565C0", background: "rgba(57,170,252,0.08)" },
              }}
            >
              <PercentIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          {/* Hide Add Client button for view_only access */}
          {!isViewOnly && (
            <Button
              variant="contained"
              onClick={handleAddClient}
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              sx={{
                background: "linear-gradient(135deg, #4db2fc, #1565C0)",
                color: "white",
                textTransform: "none",
                fontSize: "13px",
                fontWeight: 700,
                minWidth: "126px",
                height: "38px",
                borderRadius: "12px",
                boxShadow: "0 8px 20px rgba(21, 101, 192, 0.28)",
                "&:hover": {
                  background: "linear-gradient(135deg, #39aafc, #0d47a1)",
                  boxShadow: "0 10px 24px rgba(21, 101, 192, 0.38)",
                },
              }}
            >
              Add Client
            </Button>
          )}
        </Box>

      {/* ===== Portfolio Ledger — a spaced row-card list on a CSS grid,
          not a bordered spreadsheet. Two-tier grouped headers cluster the
          columns into Contract / Cash Flow / Performance zones; each client
          is its own rounded card with a radial "payment completion" ring
          around the avatar instead of a plain colored pill. ===== */}
      <Box role="table" aria-label="clients ledger" sx={{ px: "20px", pb: "18px" }}>
        {/* Column-label row — direct headers only, centered over their columns */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: LEDGER_GRID_COLS,
            alignItems: "center",
            py: "11px",
            px: "16px",
            mt: "14px",
            mb: "10px",
            borderRadius: "12px",
            background: "linear-gradient(135deg, rgba(77,178,252,0.14), rgba(21,101,192,0.08))",
            border: "1px solid rgba(57,170,252,0.18)",
          }}
        >
          <Box sx={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#5f6f86", fontFamily: "Roboto, Arial, sans-serif" }}>
            Client
          </Box>
          <SortHeader column="totalSales" icon={DescriptionOutlinedIcon}>Value</SortHeader>
          <SortHeader column="totalExpenses" icon={PaymentsOutlinedIcon}>Expenses</SortHeader>
          <SortHeader column="amountReceived" icon={AccountBalanceWalletOutlinedIcon}>Received</SortHeader>
          <SortHeader column="balanceReceivable" icon={HourglassBottomOutlinedIcon}>Receivable</SortHeader>
          <SortHeader column="balancePayable" icon={AccountBalanceOutlinedIcon}>Payable</SortHeader>
          <SortHeader column="netProfit" icon={SavingsOutlinedIcon}>Gross Profit</SortHeader>
        </Box>

        {/* Rows */}
        {allClientsSorted.length === 0 ? (
          <Box sx={{ textAlign: "center", padding: "48px", fontSize: "13px", color: "#9aa7b8" }}>
            No Active Clients
          </Box>
        ) : (
          allClientsPaged.map((client) => {
            const completionPct = client.totalSales > 0 ? (client.amountReceived / client.totalSales) * 100 : 0;
            const ringColor = completionPct >= 100 ? "#059669" : completionPct >= 40 ? "#1565C0" : "#b45309";
            return (
              <Box
                key={client.id}
                role="row"
                onClick={() => { setSelectedClientData(client); setView('detail'); }}
                sx={{
                  display: "grid",
                  gridTemplateColumns: LEDGER_GRID_COLS,
                  alignItems: "center",
                  gap: 1,
                  background: "#ffffff",
                  border: "1px solid #eef2f7",
                  borderRadius: "14px",
                  px: "16px",
                  py: "12px",
                  mb: "9px",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
                  "&:hover": { transform: "translateY(-2px)", boxShadow: "0 10px 26px rgba(13,43,92,0.09)", borderColor: "#dbe9fb" },
                  "&:hover .axd-client-name": { color: "#1565C0" },
                  "&:hover .axd-row-chevron": { opacity: 1, transform: "translateX(2px)" },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "3px",
                    background: "linear-gradient(180deg, #4db2fc, #1565C0)",
                    transform: "scaleY(0)",
                    transition: "transform 0.18s ease",
                  },
                  "&:hover::before": { transform: "scaleY(1)" },
                }}
              >
                {/* Client — radial completion ring around avatar + name + status */}
                <Box role="cell" sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                  <RadialProgress pct={completionPct} color={ringColor}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "13px",
                        fontWeight: 800,
                        color: "#1565C0",
                        background: "rgba(57, 170, 252, 0.12)",
                      }}
                    >
                      {(client.name || "C").charAt(0).toUpperCase()}
                    </Box>
                  </RadialProgress>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography noWrap className="axd-client-name" sx={{ fontSize: "13.5px", fontWeight: 700, color: "#101828", fontFamily: "Roboto, Arial, sans-serif", transition: "color 0.15s ease", lineHeight: 1.3 }}>
                      {client.name}
                    </Typography>
                    <Box
                      onClick={isViewOnly ? undefined : (e) => { e.stopPropagation(); handleStatusCellClick(e, client.id); }}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.6,
                        mt: "3px",
                        px: 1,
                        py: "2.5px",
                        borderRadius: "999px",
                        background: `${client.statusColor}22`,
                        whiteSpace: "nowrap",
                        cursor: isViewOnly ? "default" : "pointer",
                        transition: "box-shadow 0.15s ease",
                        "&:hover": isViewOnly ? {} : { boxShadow: `0 0 0 2px ${client.statusColor}55` },
                      }}
                    >
                      <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: client.statusColor, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: "#4b5b71", fontFamily: "Roboto, Arial, sans-serif" }}>
                        {statusLabelOf(client.status)}
                      </Typography>
                    </Box>
                  </Box>
                  <ChevronRightIcon className="axd-row-chevron" sx={{ fontSize: 18, color: "#c3cbd6", opacity: 0, transition: "opacity 0.18s ease, transform 0.18s ease", flexShrink: 0 }} />
                </Box>

                <Box role="cell" sx={{ textAlign: "center", fontSize: "13px", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#101828", fontFamily: "Roboto, Arial, sans-serif", whiteSpace: "nowrap" }}>
                  {formatCurrency(client.totalSales)}
                </Box>
                <Box role="cell" sx={{ textAlign: "center", fontSize: "13px", fontVariantNumeric: "tabular-nums", color: "#8b96a5", fontFamily: "Roboto, Arial, sans-serif", whiteSpace: "nowrap" }}>
                  {formatCurrency(client.totalExpenses)}
                </Box>
                <Box role="cell" sx={{ textAlign: "center", fontSize: "13px", fontVariantNumeric: "tabular-nums", color: "#059669", fontWeight: 600, fontFamily: "Roboto, Arial, sans-serif", whiteSpace: "nowrap" }}>
                  {formatCurrency(client.amountReceived)}
                </Box>
                <Box role="cell" sx={{ textAlign: "center", fontSize: "13px", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#b45309", fontFamily: "Roboto, Arial, sans-serif", whiteSpace: "nowrap" }}>
                  {formatCurrency(client.balanceReceivable)}
                </Box>
                <Box role="cell" sx={{ textAlign: "center", fontSize: "13px", fontVariantNumeric: "tabular-nums", color: "#8b96a5", fontFamily: "Roboto, Arial, sans-serif", whiteSpace: "nowrap" }}>
                  {formatCurrency(client.balancePayable)}
                </Box>

                {/* Gross Profit — amount + % chip + mini margin bar */}
                <Box role="cell" sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}>
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#101828", fontFamily: "Roboto, Arial, sans-serif", fontVariantNumeric: "tabular-nums" }}>
                      {formatCurrency(client.netProfit)}
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: "46px",
                        px: 0.75,
                        py: "3px",
                        borderRadius: "999px",
                        background: `${getPercentageColor(client.percentage)}33`,
                        color: "#344054",
                        fontWeight: 800,
                        fontSize: "11px",
                        fontFamily: "Roboto, Arial, sans-serif",
                      }}
                    >
                      {client.percentage}%
                    </Box>
                  </Box>
                  <Box sx={{ width: "64px", height: "3.5px", borderRadius: "4px", background: "#eef2f7", overflow: "hidden" }}>
                    <Box
                      sx={{
                        height: "100%",
                        width: `${Math.max(0, Math.min(100, client.percentage))}%`,
                        borderRadius: "4px",
                        background: getPercentageColor(client.percentage),
                        transition: "width 0.6s cubic-bezier(0.22,1,0.36,1)",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })
        )}

        {/* Portfolio Total — closing summary band */}
        {getSortedData().length > 0 && (() => {
          const totals = getSortedData().reduce((acc, client) => ({
            totalSales: acc.totalSales + client.totalSales,
            totalExpenses: acc.totalExpenses + client.totalExpenses,
            amountReceived: acc.amountReceived + client.amountReceived,
            balanceReceivable: acc.balanceReceivable + client.balanceReceivable,
            balancePayable: acc.balancePayable + client.balancePayable,
            netProfit: acc.netProfit + client.netProfit
          }), { totalSales: 0, totalExpenses: 0, amountReceived: 0, balanceReceivable: 0, balancePayable: 0, netProfit: 0 });
          const totalPercentage = totals.totalSales > 0 ? ((totals.netProfit / totals.totalSales) * 100).toFixed(2) : 0;

          return (
            <Box
              role="row"
              sx={{
                display: "grid",
                gridTemplateColumns: LEDGER_GRID_COLS,
                alignItems: "center",
                gap: 1,
                background: "#f5f8fd",
                border: "1px solid #dfe7f1",
                borderTop: "2px solid",
                borderImage: "linear-gradient(90deg, #4db2fc, #1565C0, #6d28d9) 1",
                borderRadius: "14px",
                px: "16px",
                py: "14px",
                mt: "6px",
              }}
            >
              <Box sx={{ fontWeight: 800, fontSize: "11.5px", color: "#5f6f86", textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: "Roboto, Arial, sans-serif" }}>
                Portfolio Total
              </Box>
              <Box sx={{ fontWeight: 800, fontSize: "13.5px", textAlign: "center", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }}>
                {formatCurrency(totals.totalSales)}
              </Box>
              <Box sx={{ fontWeight: 700, fontSize: "13px", textAlign: "center", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", color: "#5f6f86", fontFamily: "Roboto, Arial, sans-serif" }}>
                {formatCurrency(totals.totalExpenses)}
              </Box>
              <Box sx={{ fontWeight: 700, fontSize: "13px", textAlign: "center", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", color: "#059669", fontFamily: "Roboto, Arial, sans-serif" }}>
                {formatCurrency(totals.amountReceived)}
              </Box>
              <Box sx={{ fontWeight: 700, fontSize: "13px", textAlign: "center", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", color: "#b45309", fontFamily: "Roboto, Arial, sans-serif" }}>
                {formatCurrency(totals.balanceReceivable)}
              </Box>
              <Box sx={{ fontWeight: 700, fontSize: "13px", textAlign: "center", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", color: "#5f6f86", fontFamily: "Roboto, Arial, sans-serif" }}>
                {formatCurrency(totals.balancePayable)}
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "13.5px", fontWeight: 800, color: "#101828", fontFamily: "Roboto, Arial, sans-serif", fontVariantNumeric: "tabular-nums" }}>
                  {formatCurrency(totals.netProfit)}
                </Typography>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "46px",
                    px: 0.75,
                    py: "3px",
                    borderRadius: "999px",
                    background: `${getPercentageColor(parseFloat(totalPercentage))}33`,
                    color: "#101828",
                    fontWeight: 800,
                    fontSize: "11px",
                    fontFamily: "Roboto, Arial, sans-serif",
                  }}
                >
                  {totalPercentage}%
                </Box>
              </Box>
            </Box>
          );
        })()}

        {/* Pagination — the row list above is capped at 30/page; totals and
            the count badge still reflect the full filtered set. */}
        {allClientsSorted.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, padding: "14px 4px" }}>
            <Typography sx={{ fontSize: "12.5px", color: "#98a2b3", fontWeight: 600 }}>
              Showing {allClientsPageStart + 1}–{Math.min(allClientsPageStart + ALL_CLIENTS_PAGE_SIZE, allClientsSorted.length)} of {allClientsSorted.length}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                onClick={() => setAllClientsPage((p) => Math.max(1, p - 1))}
                disabled={allClientsPageClamped <= 1}
                sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#344054", background: "#f2f4f7", borderRadius: "9px", px: 1.5, py: "6px", minWidth: 0, "&.Mui-disabled": { color: "#c7ccd4", background: "#f7f8fa" } }}
              >
                Previous
              </Button>
              <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#344054", px: 1 }}>
                Page {allClientsPageClamped} of {allClientsTotalPages}
              </Typography>
              <Button
                onClick={() => setAllClientsPage((p) => Math.min(allClientsTotalPages, p + 1))}
                disabled={allClientsPageClamped >= allClientsTotalPages}
                sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#344054", background: "#f2f4f7", borderRadius: "9px", px: 1.5, py: "6px", minWidth: 0, "&.Mui-disabled": { color: "#c7ccd4", background: "#f7f8fa" } }}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      </Box>
      </>
      )}

      {/* Financial Year — desktop menu */}
      {!isMobile && (
      <Menu
        anchorEl={fyMenuAnchor}
        open={Boolean(fyMenuAnchor)}
        onClose={() => setFyMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { borderRadius: '14px', mt: 0.5, minWidth: 180, boxShadow: '0 12px 32px rgba(16,24,40,0.16)', border: '1px solid #eef2f6' } }}
      >
        {financialYears.map((year) => {
          const selected = year === financialYear;
          return (
            <MenuItem
              key={year}
              selected={selected}
              onClick={() => { if (year === 'Custom Date') { setCustomDateDialogOpen(true); } else { setFinancialYear(year); } setFyMenuAnchor(null); }}
              sx={{ fontSize: '13px', fontWeight: selected ? 700 : 500, color: selected ? '#1565C0' : '#344054', fontFamily: 'Roboto, Arial, sans-serif', py: 1, '&.Mui-selected': { background: '#eef6ff' }, '&.Mui-selected:hover': { background: '#e2effe' } }}
            >
              <CheckIcon sx={{ fontSize: 16, mr: 1, visibility: selected ? 'visible' : 'hidden', color: '#39aafc' }} />
              {year === 'Custom Date' ? 'Custom Date…' : `FY ${year}`}
            </MenuItem>
          );
        })}
        {financialYear && (
          <MenuItem
            onClick={() => { handleClearFinancialYear(); setFyMenuAnchor(null); }}
            sx={{ fontSize: '13px', color: '#d92d20', fontWeight: 600, fontFamily: 'Roboto, Arial, sans-serif', py: 1 }}
          >
            <ClearIcon sx={{ fontSize: 16, mr: 1 }} />
            Clear filter
          </MenuItem>
        )}
      </Menu>
      )}
      {/* Financial Year — mobile top sheet */}
      <Drawer anchor="top" open={isMobile && Boolean(fyMenuAnchor)} onClose={() => setFyMenuAnchor(null)} PaperProps={{ sx: topSheetPaperSx }}>
        <Typography sx={sheetTitleSx}>Financial Year</Typography>
        {financialYears.map((year) =>
          renderPlainSheetOption(
            year,
            year === 'Custom Date' ? 'Custom Date…' : `FY ${year}`,
            year === financialYear,
            () => { if (year === 'Custom Date') { setCustomDateDialogOpen(true); } else { setFinancialYear(year); } setFyMenuAnchor(null); }
          )
        )}
        {financialYear && (
          <Box
            onClick={() => { handleClearFinancialYear(); setFyMenuAnchor(null); }}
            sx={{ py: 1.4, px: 1.25, borderRadius: '14px', cursor: 'pointer', transition: 'background 0.15s ease', '&:active': { background: '#fef3f2' } }}
          >
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#d92d20', fontFamily: 'Roboto, Arial, sans-serif' }}>Clear filter</Typography>
          </Box>
        )}
        {bottomHandle}
      </Drawer>

      {/* Status Filter — desktop menu */}
      {!isMobile && (
      <Menu
        anchorEl={statusAnchorEl}
        open={Boolean(statusAnchorEl)}
        onClose={handleStatusFilterClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: "14px", mt: 0.5, minWidth: 190, boxShadow: "0 12px 32px rgba(16,24,40,0.16)", border: "1px solid #eef2f6" } }}
      >
        {statusOptions.map((option) => {
          const active = option.value === "all" ? !selectedStatus : selectedStatus === option.value;
          return (
            <MenuItem
              key={option.value}
              selected={active}
              onClick={() => handleStatusFilterSelect(option.value)}
              sx={{ fontSize: "13px", fontWeight: active ? 700 : 500, color: active ? "#1565C0" : "#344054", fontFamily: "Roboto, Arial, sans-serif", py: 1, "&.Mui-selected": { background: "#eef6ff" }, "&.Mui-selected:hover": { background: "#e2effe" } }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: option.color === "#FFFFFF" ? "#e4e7ec" : option.color, marginRight: "12px", flexShrink: 0, boxShadow: option.color !== "#FFFFFF" ? `0 0 0 3px ${option.color}26` : "none" }} />
              {option.label}
              {active && <CheckIcon sx={{ fontSize: 16, ml: "auto", pl: 1, color: "#39aafc" }} />}
            </MenuItem>
          );
        })}
      </Menu>
      )}
      {/* Status Filter — mobile top sheet */}
      <Drawer anchor="top" open={isMobile && Boolean(statusAnchorEl)} onClose={handleStatusFilterClose} PaperProps={{ sx: topSheetPaperSx }}>
        <Typography sx={sheetTitleSx}>Filter by Status</Typography>
        {statusOptions.map((opt) => renderSheetOption(opt.value, opt.color, opt.label, opt.value === 'all' ? !selectedStatus : selectedStatus === opt.value, () => handleStatusFilterSelect(opt.value)))}
        {bottomHandle}
      </Drawer>

      {/* Status Change — desktop menu */}
      {!isMobile && (
      <Menu
        anchorEl={statusChangeAnchorEl}
        open={Boolean(statusChangeAnchorEl)}
        onClose={handleStatusChangeClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: "14px", mt: 0.5, minWidth: 190, boxShadow: "0 12px 32px rgba(16,24,40,0.16)", border: "1px solid #eef2f6" } }}
      >
        {statusOptions.filter(opt => opt.value !== "all").map((option) => {
          const active = currentChangeStatus === option.value;
          return (
            <MenuItem
              key={option.value}
              selected={active}
              onClick={() => handleStatusChange(option.value)}
              sx={{ fontSize: "13px", fontWeight: active ? 700 : 500, color: active ? "#1565C0" : "#344054", fontFamily: "Roboto, Arial, sans-serif", py: 1, "&.Mui-selected": { background: "#eef6ff" }, "&.Mui-selected:hover": { background: "#e2effe" } }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: option.color, marginRight: "12px", flexShrink: 0, boxShadow: `0 0 0 3px ${option.color}26` }} />
              {option.label}
              {active && <CheckIcon sx={{ fontSize: 16, ml: "auto", pl: 1, color: "#39aafc" }} />}
            </MenuItem>
          );
        })}
      </Menu>
      )}
      {/* Status Change — mobile top sheet */}
      <Drawer anchor="top" open={isMobile && Boolean(statusChangeAnchorEl)} onClose={handleStatusChangeClose} PaperProps={{ sx: topSheetPaperSx }}>
        <Typography sx={sheetTitleSx}>Change Project Status</Typography>
        {statusOptions.filter((opt) => opt.value !== 'all').map((opt) => renderSheetOption(opt.value, opt.color, opt.label, currentChangeStatus === opt.value, () => handleStatusChange(opt.value)))}
        {bottomHandle}
      </Drawer>

      {/* Profit % Filter — desktop menu */}
      {!isMobile && (
      <Menu
        anchorEl={percentageFilterAnchorEl}
        open={Boolean(percentageFilterAnchorEl)}
        onClose={handlePercentageFilterClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: "14px", mt: 0.5, minWidth: 190, boxShadow: "0 12px 32px rgba(16,24,40,0.16)", border: "1px solid #eef2f6" } }}
      >
        {percentageFilterOptions.map((option) => {
          const active = option.value === "all" ? !selectedPercentageFilter : selectedPercentageFilter === option.value;
          return (
            <MenuItem
              key={option.value}
              selected={active}
              onClick={() => handlePercentageFilterSelect(option.value)}
              sx={{ fontSize: "13px", fontWeight: active ? 700 : 500, color: active ? "#1565C0" : "#344054", fontFamily: "Roboto, Arial, sans-serif", py: 1, "&.Mui-selected": { background: "#eef6ff" }, "&.Mui-selected:hover": { background: "#e2effe" } }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: option.color === "#FFFFFF" ? "#e4e7ec" : option.color, marginRight: "12px", flexShrink: 0, boxShadow: option.color !== "#FFFFFF" ? `0 0 0 3px ${option.color}26` : "none" }} />
              {option.label}
              {active && <CheckIcon sx={{ fontSize: 16, ml: "auto", pl: 1, color: "#39aafc" }} />}
            </MenuItem>
          );
        })}
      </Menu>
      )}
      {/* Profit % Filter — mobile top sheet */}
      <Drawer anchor="top" open={isMobile && Boolean(percentageFilterAnchorEl)} onClose={handlePercentageFilterClose} PaperProps={{ sx: topSheetPaperSx }}>
        <Typography sx={sheetTitleSx}>Filter by Profit</Typography>
        {percentageFilterOptions.map((opt) => renderSheetOption(opt.value, opt.color, opt.label, opt.value === 'all' ? !selectedPercentageFilter : selectedPercentageFilter === opt.value, () => handlePercentageFilterSelect(opt.value)))}
        {bottomHandle}
      </Drawer>

      {/* Custom Date Dialog */}
      <Dialog
        open={customDateDialogOpen}
        onClose={handleCustomDateCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
      >
        <Box sx={{ background: "linear-gradient(155deg, #4db2fc 0%, #1565C0 100%)", pt: 3.5, pb: 3, px: 3, color: "#fff", textAlign: "center" }}>
          <Box sx={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <CalendarMonthIcon sx={{ fontSize: 26 }} />
          </Box>
          <Typography sx={{ fontSize: "18px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif" }}>
            Custom Date Range
          </Typography>
          <Typography sx={{ fontSize: "12.5px", opacity: 0.9, fontFamily: "Roboto, Arial, sans-serif", mt: "4px" }}>
            Filter clients between two dates
          </Typography>
        </Box>
        <DialogContent sx={{ pt: "24px !important", px: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
              fullWidth
            />
            <TextField
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
          <Button
            onClick={handleCustomDateCancel}
            fullWidth
            sx={{ textTransform: "none", fontWeight: 700, fontSize: "14px", color: "#667085", background: "#f2f4f7", borderRadius: "14px", py: 1.2, "&:hover": { background: "#e4e7ec" } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCustomDateSearch}
            disabled={!fromDate || !toDate}
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 800,
              fontSize: "14px",
              color: "#fff",
              background: "linear-gradient(135deg,#4db2fc,#1565C0)",
              borderRadius: "14px",
              py: 1.2,
              boxShadow: "0 8px 20px rgba(21,101,192,0.35)",
              "&:hover": { background: "linear-gradient(135deg,#39aafc,#0d47a1)" },
              "&.Mui-disabled": { background: "#e4e7ec", color: "#98a2b3" },
            }}
          >
            Search
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Client Dialog */}
      <Dialog
        open={addClientDialogOpen}
        onClose={handleAddClientClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: isMobile ? {} : { borderRadius: "24px", overflow: "hidden" } }}
      >
        {isMobile ? (
          <Box sx={{ minHeight: '100%', background: '#f4f5f7' }}>
            {/* Gradient header — close + title inline, subtitle below, with an icon badge */}
            <Box sx={{ background: 'linear-gradient(150deg, #4db2fc 0%, #1f8fe8 100%)', color: '#fff', pt: 'max(14px, env(safe-area-inset-top))', px: 2, pb: 3, borderBottomLeftRadius: '26px', borderBottomRightRadius: '26px', boxShadow: '0 8px 24px rgba(31,143,232,0.3)', position: 'relative', overflow: 'hidden' }}>
              <Box sx={{ position: 'absolute', top: '-30px', right: '-20px', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.12)' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <IconButton onClick={handleAddClientClose} sx={{ color: '#fff', p: '6px' }}><CloseIcon /></IconButton>
                  <Typography noWrap sx={{ fontSize: '20px', fontWeight: 800, fontFamily: 'Roboto, Arial, sans-serif' }}>Add New Client</Typography>
                </Box>
                <Box sx={{ width: 44, height: 44, borderRadius: '14px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <PersonAddAlt1Icon sx={{ fontSize: 24 }} />
                </Box>
              </Box>
              <Typography sx={{ fontSize: '13px', opacity: 0.9, mt: 0.75, ml: '6px', position: 'relative', fontFamily: 'Roboto, Arial, sans-serif' }}>Create a new project profile</Typography>
            </Box>

            {/* Fields start cleanly below the header */}
            <Box sx={{ px: 2.5, pt: 3, pb: 'max(24px, env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 2.25 }}>
              <TextField label="Date *" type="date" value={newClient.date} onChange={(e) => handleNewClientChange('date', e.target.value)} error={!!fieldErrors.date} helperText={fieldErrors.date} InputLabelProps={{ shrink: true }} sx={addFieldSx} fullWidth />
              <TextField label="Client Name *" value={newClient.clientName} onChange={(e) => handleNewClientChange('clientName', e.target.value)} error={!!fieldErrors.clientName} helperText={fieldErrors.clientName} sx={addFieldSx} fullWidth />
              <TextField label="Project ID" value={newClient.projectId} onChange={(e) => handleNewClientChange('projectId', e.target.value)} sx={addFieldSx} fullWidth />
              <TextField label="Address *" value={newClient.address} onChange={(e) => handleNewClientChange('address', e.target.value)} error={!!fieldErrors.address} helperText={fieldErrors.address} sx={addFieldSx} fullWidth multiline rows={2} />
              <TextField label="Mobile Number *" value={newClient.mobileNumber} onChange={(e) => handleNewClientChange('mobileNumber', e.target.value)} error={!!fieldErrors.mobileNumber} helperText={fieldErrors.mobileNumber} inputProps={{ inputMode: 'numeric' }} sx={addFieldSx} fullWidth />
              <TextField label="Email ID" value={newClient.emailId} onChange={(e) => handleNewClientChange('emailId', e.target.value)} sx={addFieldSx} fullWidth />

              <Button
                onClick={handleAddClientNext}
                disabled={loading}
                fullWidth
                sx={{ mt: 1, py: 1.6, borderRadius: '14px', background: 'linear-gradient(135deg, #39aafc 0%, #1f8fe8 100%)', color: '#fff', fontSize: '16px', fontWeight: 700, textTransform: 'none', fontFamily: 'Roboto, Arial, sans-serif', boxShadow: '0 8px 20px rgba(31,143,232,0.4)', '&:hover': { background: 'linear-gradient(135deg, #39aafc 0%, #1f8fe8 100%)' }, '&:active': { transform: 'scale(0.99)' }, '&.Mui-disabled': { background: '#c4c9d2', color: '#fff' } }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Add Client'}
              </Button>
            </Box>
          </Box>
        ) : (
        <>
        {/* Desktop: gradient signature header + two-column field grid */}
        <Box sx={{ background: "linear-gradient(155deg, #4db2fc 0%, #1565C0 100%)", pt: 3.5, pb: 3, px: 3, color: "#fff", textAlign: "center" }}>
          <Box sx={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <PersonAddAlt1Icon sx={{ fontSize: 25 }} />
          </Box>
          <Typography sx={{ fontSize: "18px", fontWeight: 800, fontFamily: "Roboto, sans-serif" }}>
            Add New Client
          </Typography>
          <Typography sx={{ fontSize: "12.5px", opacity: 0.9, fontFamily: "Roboto, sans-serif", mt: "4px" }}>
            Create a new project profile
          </Typography>
        </Box>
        <DialogContent sx={{ pt: "24px !important", px: 3 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="Date *"
              type="date"
              value={newClient.date}
              onChange={(e) => handleNewClientChange("date", e.target.value)}
              error={!!fieldErrors.date}
              helperText={fieldErrors.date}
              InputLabelProps={{ shrink: true, style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" } }}
              inputProps={{ style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" } }}
              InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
              FormHelperTextProps={{ style: { fontSize: "12px", fontFamily: "Roboto, Arial, sans-serif" } }}
              fullWidth
            />
            <TextField
              label="Client Name *"
              value={newClient.clientName}
              onChange={(e) => handleNewClientChange("clientName", e.target.value)}
              error={!!fieldErrors.clientName}
              helperText={fieldErrors.clientName}
              InputLabelProps={{ style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" } }}
              inputProps={{ style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" } }}
              InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
              FormHelperTextProps={{ style: { fontSize: "12px", fontFamily: "Roboto, Arial, sans-serif" } }}
              fullWidth
            />
            <TextField
              label="Project ID"
              value={newClient.projectId}
              onChange={(e) => handleNewClientChange("projectId", e.target.value)}
              InputLabelProps={{ style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" } }}
              inputProps={{ style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" } }}
              InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
              fullWidth
            />
            <TextField
              label="Mobile Number *"
              value={newClient.mobileNumber}
              onChange={(e) => handleNewClientChange("mobileNumber", e.target.value)}
              error={!!fieldErrors.mobileNumber}
              helperText={fieldErrors.mobileNumber}
              InputLabelProps={{ style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" } }}
              inputProps={{ style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" } }}
              InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
              FormHelperTextProps={{ style: { fontSize: "12px", fontFamily: "Roboto, Arial, sans-serif" } }}
              fullWidth
            />
            <Box sx={{ gridColumn: "1 / -1" }}>
              <TextField
                label="Address *"
                value={newClient.address}
                onChange={(e) => handleNewClientChange("address", e.target.value)}
                error={!!fieldErrors.address}
                helperText={fieldErrors.address}
                InputLabelProps={{ style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" } }}
                inputProps={{ style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" } }}
                InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
                FormHelperTextProps={{ style: { fontSize: "12px", fontFamily: "Roboto, Arial, sans-serif" } }}
                fullWidth
                multiline
                rows={2}
              />
            </Box>
            <Box sx={{ gridColumn: "1 / -1" }}>
              <TextField
                label="Email ID"
                value={newClient.emailId}
                onChange={(e) => handleNewClientChange("emailId", e.target.value)}
                InputLabelProps={{ style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" } }}
                inputProps={{ style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" } }}
                InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
                fullWidth
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
          <Button
            onClick={handleAddClientClose}
            disabled={loading}
            fullWidth
            sx={{ textTransform: "none", fontWeight: 700, fontSize: "14px", color: "#667085", background: "#f2f4f7", borderRadius: "14px", py: 1.2, "&:hover": { background: "#e4e7ec" } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddClientNext}
            disabled={loading}
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 800,
              fontSize: "14px",
              color: "#fff",
              background: "linear-gradient(135deg,#4db2fc,#1565C0)",
              borderRadius: "14px",
              py: 1.2,
              boxShadow: "0 8px 20px rgba(21,101,192,0.35)",
              "&:hover": { background: "linear-gradient(135deg,#39aafc,#0d47a1)" },
              "&.Mui-disabled": { background: "#e4e7ec", color: "#98a2b3" },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Next"}
          </Button>
        </DialogActions>
        </>
        )}
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AllClients;
